import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { ReactNode, useEffect } from "react";
import { ThemeContextProvider } from "./ThemeContext";
import { useV2Theme } from "../hooks/useV2Theme";
import { v2DarkTheme, v2LightTheme } from "./v2Theme";
import { darkTokens, lightTokens } from "./v2Tokens";

function applyTokens(target: HTMLElement, tokens: Record<string, string>): void {
  for (const [key, value] of Object.entries(tokens)) {
    target.style.setProperty(key, value);
  }
}

function InnerProvider({ children }: { children: ReactNode }) {
  const { mode } = useV2Theme();
  const muiTheme = mode === "dark" ? v2DarkTheme : v2LightTheme;
  const tokens = mode === "dark" ? darkTokens : lightTokens;

  // Apply tokens + data-theme to <html> so portal-rendered MUI components
  // (Popover, Menu, Dialog) — which are appended to document.body — can
  // resolve var(--*) tokens. The wrapper <div> below scopes the same data
  // for in-tree consumers.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", mode);
    applyTokens(root, tokens);
  }, [mode, tokens]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {mode === "dark" && (
        <style>{`
          [data-theme="dark"] *::-webkit-scrollbar { width: 8px; height: 8px; }
          [data-theme="dark"] *::-webkit-scrollbar-track { background: transparent; }
          [data-theme="dark"] *::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.2); border-radius: 4px; }
          [data-theme="dark"] *::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,0.35); }
          [data-theme="dark"] *::-webkit-scrollbar-corner { background: transparent; }
        `}</style>
      )}
      <div data-theme={mode} style={tokens as unknown as React.CSSProperties}>
        {children}
      </div>
    </ThemeProvider>
  );
}

export function V2ThemeProvider({ children }: { children: ReactNode }) {
  return (
    <ThemeContextProvider>
      <InnerProvider>{children}</InnerProvider>
    </ThemeContextProvider>
  );
}
